import * as React from 'react';
import { FieldValues } from 'react-hook-form';

import { Box } from './box';
import { VStack } from './stack';
import { Text } from './text';
import { Separator } from './separator';
import { Container } from './container';
import { StepComponentProps, useMultiStepFormOptional } from './multi-step-form';
import { StepNavigationButtons } from './step-navigator';

export interface FormStepProps<T extends FieldValues = FieldValues>
  extends Omit<StepComponentProps<T>, 'formMethods' | 'currentStepData' | 'allFormData'> {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  showNavigationButtons?: boolean;
  navigationButtonsProps?: React.ComponentProps<typeof StepNavigationButtons>;
  className?: string;
  containerProps?: React.ComponentProps<typeof Container>;
  showStepInfo?: boolean;
  alertMessage?: string;
  alertStatus?: 'info' | 'warning' | 'success' | 'error';
}

/**
 * FormStep - Wrapper component for individual step content
 *
 * Features:
 * - Standardized layout for step content
 * - Built-in navigation buttons
 * - Step title and subtitle support
 * - Alert messages for step-specific information
 * - Responsive container layout
 * - Integration with multi-step form context
 *
 * @example
 * ```tsx
 * const PersonalInfoStep = ({ isActive }) => (
 *   <FormStep
 *     title="Personal Information"
 *     subtitle="Please provide your basic details"
 *     showNavigationButtons
 *     alertMessage="All fields are required for verification"
 *     alertStatus="info"
 *   >
 *     <SimpleGrid columns={2} spacing={4}>
 *       <FormField name="firstName" form={form}>
 *         <Field.Root>
 *           <Field.Label>First Name</Field.Label>
 *           <Field.Control>
 *             {(field) => <Input {...field} />}
 *           </Field.Control>
 *           <Field.ErrorText />
 *         </Field.Root>
 *       </FormField>
 *       {/* More fields... *\/}
 *     </SimpleGrid>
 *   </FormStep>
 * );
 * ```
 */
export const FormStep = <T extends FieldValues = FieldValues>({
  children,
  title,
  subtitle,
  showNavigationButtons = true,
  navigationButtonsProps = {},
  className,
  containerProps,
  showStepInfo = false,
  alertMessage,
  alertStatus = 'info',
  isActive: _isActive,
  onNext,
  onPrevious,
}: FormStepProps<T>) => {
  // Optional context usage - won't throw error if not within MultiStepForm
  const context = useMultiStepFormOptional<T>();
  const currentStep = context?.currentStep ?? 0;
  const totalSteps = context?.totalSteps ?? 1;

  return (
    <Box className={className} width="full">
      <Container maxWidth="2xl" {...containerProps}>
        <VStack gap={6} align="stretch">
          {/* Step Header */}
          {(title || subtitle || showStepInfo) && (
            <Box>
              {showStepInfo && (
                <Text fontSize="sm" color="#6B7280" marginBottom={2}>
                  Step {currentStep + 1} of {totalSteps}
                </Text>
              )}

              {title && (
                <Text fontSize="xl" fontWeight="bold" marginBottom={2}>
                  {title}
                </Text>
              )}

              {subtitle && (
                <Text color="#4B5563" fontSize="md">
                  {subtitle}
                </Text>
              )}
            </Box>
          )}

          {/* Alert Message */}
          {alertMessage && (
            <Box
              border="1px solid"
              borderRadius="md"
              padding="16px"
              backgroundColor={
                alertStatus === 'info'
                  ? '#EBF8FF'
                  : alertStatus === 'warning'
                    ? '#FFFAF0'
                    : alertStatus === 'success'
                      ? '#F0FDF4'
                      : '#FEF2F2'
              }
              borderColor={
                alertStatus === 'info'
                  ? '#90CDF4'
                  : alertStatus === 'warning'
                    ? '#FBBF24'
                    : alertStatus === 'success'
                      ? '#86EFAC'
                      : '#FCA5A5'
              }
            >
              <Text
                fontSize="sm"
                color={
                  alertStatus === 'info'
                    ? '#1E3A8A'
                    : alertStatus === 'warning'
                      ? '#92400E'
                      : alertStatus === 'success'
                        ? '#166534'
                        : '#991B1B'
                }
              >
                {alertMessage}
              </Text>
            </Box>
          )}

          {/* Divider */}
          {(title || subtitle || alertMessage) && <Separator />}

          {/* Step Content */}
          <Box flex={1}>{children}</Box>

          {/* Navigation Buttons */}
          {showNavigationButtons && (
            <>
              <Separator />
              <StepNavigationButtons
                onNext={onNext}
                onPrevious={onPrevious}
                {...navigationButtonsProps}
              />
            </>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

/**
 * FormStepContent - Simple wrapper for step content without navigation
 *
 * Use this when you want to handle navigation manually or have custom navigation
 *
 * @example
 * ```tsx
 * const CustomStep = () => (
 *   <FormStepContent
 *     title="Custom Step"
 *     alertMessage="This step has custom navigation"
 *   >
 *     <Box>Step content here</Box>
 *     <CustomNavigationComponent />
 *   </FormStepContent>
 * );
 * ```
 */
export const FormStepContent = <T extends FieldValues = FieldValues>({
  children,
  title,
  subtitle,
  className,
  containerProps,
  showStepInfo = false,
  alertMessage,
  alertStatus = 'info',
}: Omit<
  FormStepProps<T>,
  'showNavigationButtons' | 'navigationButtonsProps' | 'onNext' | 'onPrevious' | 'isActive'
>) => {
  const context = useMultiStepFormOptional<T>();
  const currentStep = context?.currentStep ?? 0;
  const totalSteps = context?.totalSteps ?? 1;

  return (
    <Box className={className} width="full">
      <Container maxWidth="2xl" {...containerProps}>
        <VStack gap={6} align="stretch">
          {/* Step Header */}
          {(title || subtitle || showStepInfo) && (
            <Box>
              {showStepInfo && (
                <Text fontSize="sm" color="#6B7280" marginBottom={2}>
                  Step {currentStep + 1} of {totalSteps}
                </Text>
              )}

              {title && (
                <Text fontSize="xl" fontWeight="bold" marginBottom={2}>
                  {title}
                </Text>
              )}

              {subtitle && (
                <Text color="#4B5563" fontSize="md">
                  {subtitle}
                </Text>
              )}
            </Box>
          )}

          {/* Alert Message */}
          {alertMessage && (
            <Box
              border="1px solid"
              borderRadius="md"
              padding="16px"
              backgroundColor={
                alertStatus === 'info'
                  ? '#EBF8FF'
                  : alertStatus === 'warning'
                    ? '#FFFAF0'
                    : alertStatus === 'success'
                      ? '#F0FDF4'
                      : '#FEF2F2'
              }
              borderColor={
                alertStatus === 'info'
                  ? '#90CDF4'
                  : alertStatus === 'warning'
                    ? '#FBBF24'
                    : alertStatus === 'success'
                      ? '#86EFAC'
                      : '#FCA5A5'
              }
            >
              <Text
                fontSize="sm"
                color={
                  alertStatus === 'info'
                    ? '#1E3A8A'
                    : alertStatus === 'warning'
                      ? '#92400E'
                      : alertStatus === 'success'
                        ? '#166534'
                        : '#991B1B'
                }
              >
                {alertMessage}
              </Text>
            </Box>
          )}

          {/* Divider */}
          {(title || subtitle || alertMessage) && <Separator />}

          {/* Step Content */}
          <Box flex={1}>{children}</Box>
        </VStack>
      </Container>
    </Box>
  );
};

/**
 * useFormStepHelpers - Hook for accessing step-specific utilities
 *
 * Provides helpful utilities for working within form steps
 */
export const useFormStepHelpers = <T extends FieldValues = FieldValues>() => {
  const context = useMultiStepFormOptional<T>();

  if (!context) {
    return {
      isStepValid: false,
      stepNumber: 1,
      progressPercentage: 0,
      getStepData: () => ({}),
      validateCurrentStep: async () => false,
      canNavigateToStep: () => false,
      saveStepData: () => {},
    };
  }

  return {
    ...context,

    // Additional step-specific helpers
    isStepValid: context.canGoNext,
    stepNumber: context.currentStep + 1,
    progressPercentage: Math.round(((context.currentStep + 1) / context.totalSteps) * 100),

    // Step data helpers
    getStepData: (stepIndex?: number) => {
      if (stepIndex !== undefined) {
        // Would need to implement step-specific data retrieval
        return context.getAllFormData();
      }
      return context.getCurrentStepData();
    },

    // Validation helpers
    validateCurrentStep: async () => {
      // This would be implemented with form validation
      return context.canGoNext;
    },

    // Navigation helpers
    canNavigateToStep: (stepIndex: number) => {
      // Can navigate to current step or any previous step
      return stepIndex <= context.currentStep;
    },

    // Data persistence helpers
    saveStepData: (data: Partial<T>) => {
      context.updateFormData(data);
    },
  };
};
